import { useRef } from 'react';
import { Autocomplete, GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import locationIcon from '../../../assets/images/location-marker.svg';
import locationHighlighterIcon from '../../../assets/images/location-marker.svg';

const containerStyle = {
    width: '100%',
    height: '400px'
};

const defaultCenter = {
    lat: 51.5074,
    lng: -0.1278
};

const extractAddressComponents = (place) => {
    const addressComponents = {
        city: '',
        state: '',
        country: '',
        zipCode: ''
    };

    if (place.address_components) {
        place.address_components.forEach((component) => {
            const types = component.types;
            if (types.includes('locality')) {
                addressComponents.city = component.long_name;
            }
            if (types.includes('administrative_area_level_1')) {
                addressComponents.state = component.long_name;
            }
            if (types.includes('country')) {
                addressComponents.country = component.long_name;
            }
            if (types.includes('postal_code')) {
                addressComponents.zipCode = component.long_name;
            }
        });
    }

    return addressComponents;
};

const AddressInputWithMapClick = ({ errors, values, setFieldValue, withoutMap, label }) => {
    const autocompleteRef = useRef(null);
    const geocoder = useRef(null);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries: ['places']
    });

    const handlePlaceSelect = () => {
        const place = autocompleteRef.current.getPlace();
        if (place) {
            setFieldValue('address', place.formatted_address || '');
            const lat = place.geometry?.location?.lat();
            const lng = place.geometry?.location?.lng();
            if (lat && lng) {
                setFieldValue('location', { lat, lng });
            }
            const { city, state, country, zipCode } = extractAddressComponents(place);
            setFieldValue('city', city);
            setFieldValue('state', state);
            setFieldValue('country', country);
            setFieldValue('zipCode', zipCode);
        }
    };

    const handleMapClick = (event) => {
        const { latLng } = event;
        const lat = latLng.lat();
        const lng = latLng.lng();

        if (!geocoder.current) {
            geocoder.current = new window.google.maps.Geocoder();
        }
        geocoder.current.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
                setFieldValue('address', results[0].formatted_address);
                setFieldValue('location', { lat, lng });
                const { city, state, country, zipCode } = extractAddressComponents(results[0]);
                setFieldValue('city', city);
                setFieldValue('state', state);
                setFieldValue('country', country);
                setFieldValue('zipCode', zipCode);
            }
        });
    };

    const handleLatLngChange = (e) => {
        const { name, value } = e.target;
        const newLocation = { ...values.location, [name]: parseFloat(value) };
        setFieldValue('location', newLocation);

        if (!geocoder.current) {
            geocoder.current = new window.google.maps.Geocoder();
        }
        geocoder.current.geocode({ location: newLocation }, (results, status) => {
            if (status === 'OK' && results[0]) {
                setFieldValue('address', results[0].formatted_address);
            }
        });
    };

    if (!isLoaded) {
        return <div>Loading map...</div>; // Render a fallback while the API is loading
    }

    const mapCenter = values?.location?.lat && values?.location?.lng ? values.location : defaultCenter;

    return (
        <div className="mt-3 w-100 address-input-box">
            <label htmlFor="address" className="form-label">
                {label || 'Parking Address*'}
            </label>
            <div className="d-flex flex-column w-100">
                <Autocomplete onLoad={(autocompleteInstance) => (autocompleteRef.current = autocompleteInstance)} onPlaceChanged={handlePlaceSelect}>
                    <div className="w-100">
                        <div className=" w-100 d-flex">
                            <div className="location-icon-box">
                                <img src={locationIcon} alt="Location" height={25} />
                            </div>
                            <input
                                style={{ width: '100%' }}
                                id="address"
                                type="text"
                                className="google-address-form-control"
                                placeholder="Enter address"
                                value={values?.address}
                                onChange={(e) => setFieldValue('address', e.target.value)}
                            />
                        </div>
                        {errors?.address && <div className="validation-error invalid-feedback d-block">{errors?.address}</div>}
                    </div>
                </Autocomplete>

                <div className="d-flex justify-content-end gap-3 mt-2 w-100">
                    <input
                        id="lat"
                        type="number"
                        className="form-control"
                        placeholder="Latitude"
                        name="lat"
                        value={values?.location?.lat || ''}
                        onChange={handleLatLngChange}
                    />
                    <input
                        id="lng"
                        type="number"
                        className="form-control"
                        placeholder="Longitude"
                        name="lng"
                        value={values?.location?.lng || ''}
                        onChange={handleLatLngChange}
                    />
                </div>
            </div>
            {!withoutMap && (
                <div className="mt-3">
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={mapCenter}
                        zoom={values?.location?.lat && values?.location?.lng ? 15 : 10} // Adjust zoom based on whether location is provided
                        onClick={handleMapClick}
                        options={{
                            streetViewControl: true,
                            zoomControl: true,
                            mapTypeControl: false,
                            fullscreenControl: false
                        }}
                    >
                        {mapCenter && (
                            <Marker
                                key={`${mapCenter.lat}-${mapCenter.lng}`}
                                icon={{
                                    url: locationHighlighterIcon,
                                    scaledSize: new window.google.maps.Size(40, 40)
                                }}
                                position={mapCenter}
                            />
                        )}
                    </GoogleMap>
                </div>
            )}
        </div>
    );
};

export default AddressInputWithMapClick;
